import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, X, ChevronDown, Grid, List, Heart } from 'lucide-react';
import { categories, collections as siteCollections } from '../data/mockData';
import CollectionStoryCircles from '../components/CollectionStoryCircles';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import InteractiveProductImage from '../components/InteractiveProductImage';
import { getImageUrl } from '../lib/utils';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [stockData, setStockData] = useState({});
  const [priceFilter, setPriceFilter] = useState([]);
  const [sizeFilter, setSizeFilter] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const { isAuthenticated, login } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();

  const selectedCategory = searchParams.get('category') || 'all';
  const selectedCollection = searchParams.get('collection') || 'all';
  const searchQuery = searchParams.get('search') || '';
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${backendUrl}/api/products`);
        if (response.ok) {
          const data = await response.json();
          setProducts(data);
        }
      } catch (error) {
        console.error('Products fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [backendUrl]);

  // Fetch stock data
  useEffect(() => {
    const fetchStock = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/stock/all`);
        if (response.ok) {
          const data = await response.json();
          const stockMap = {};
          data.forEach(item => {
            if (!stockMap[item.product_id]) {
              stockMap[item.product_id] = {};
            }
            stockMap[item.product_id][item.size] = item.quantity - (item.reserved || 0);
          });
          setStockData(stockMap);
        }
      } catch (error) {
        console.error('Stock fetch error:', error);
      }
    };
    fetchStock();
  }, [backendUrl]);

  // Initialize stock on first load
  useEffect(() => {
    const initStock = async () => {
      try {
        await fetch(`${backendUrl}/api/stock/init`, { method: 'POST' });
      } catch (error) {
        console.error('Stock init error:', error);
      }
    };
    initStock();
  }, [backendUrl]);

  // Check if product has any stock
  const hasStock = (productId) => {
    // First check mock data stock
    const product = products.find(p => p.id === productId);
    if (product?.stock) {
      const totalMockStock = Object.values(product.stock).reduce((sum, qty) => sum + qty, 0);
      if (totalMockStock > 0) {
        // If we have backend stock data, use it
        if (stockData[productId]) {
          const totalBackendStock = Object.values(stockData[productId]).reduce((sum, qty) => sum + qty, 0);
          return totalBackendStock > 0;
        }
        return true; // Use mock data if no backend data
      }
    }
    // Check backend stock
    if (stockData[productId]) {
      const total = Object.values(stockData[productId]).reduce((sum, qty) => sum + qty, 0);
      return total > 0;
    }
    return true; // Default to in stock
  };

  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter((p) => p.category === selectedCategory);
    }

    // Filter by collection
    if (selectedCollection !== 'all') {
      result = result.filter((p) => (p.collection || 'kokten-isiga') === selectedCollection);
    }

    // Filter by price
    if (priceFilter.length > 0) {
      result = result.filter(p => {
        const price = p.prices?.['65x65'] || p.price;
        return priceFilter.some(range => {
          if (range === '0-2000') return price >= 0 && price <= 2000;
          if (range === '2000-3000') return price > 2000 && price <= 3000;
          if (range === '3000-4000') return price > 3000 && price <= 4000;
          if (range === '4000+') return price > 4000;
          return false;
        });
      });
    }

    // Filter by size
    if (sizeFilter.length > 0) {
      result = result.filter(p => 
        p.sizes?.some(size => sizeFilter.includes(size))
      );
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => (a.prices?.['65x65'] || a.price) - (b.prices?.['65x65'] || b.price));
        break;
      case 'price-high':
        result.sort((a, b) => (b.prices?.['65x65'] || b.price) - (a.prices?.['65x65'] || a.price));
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'newest':
      default:
        result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    }

    return result;
  }, [products, selectedCategory, selectedCollection, sortBy, searchQuery, priceFilter, sizeFilter]);

  const handleCategoryChange = (categoryId) => {
    if (categoryId === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams);
  };

  const handlePriceFilter = (range) => {
    setPriceFilter(prev => 
      prev.includes(range) 
        ? prev.filter(r => r !== range)
        : [...prev, range]
    );
  };

  const handleSizeFilter = (size) => {
    setSizeFilter(prev =>
      prev.includes(size)
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  const handleFavoriteClick = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      login();
      return;
    }
    
    await toggleFavorite(productId);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const clearFilters = () => {
    setPriceFilter([]);
    setSizeFilter([]);
    searchParams.delete('category');
    searchParams.delete('search');
    setSearchParams(searchParams);
  };

  const hasActiveFilters = priceFilter.length > 0 || sizeFilter.length > 0 || selectedCategory !== 'all' || searchQuery;

  return (
    <div className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Collections as Instagram-style story circles */}
        <CollectionStoryCircles
          activeSlug={selectedCollection}
          onSelectAll={() => { searchParams.delete('collection'); setSearchParams(searchParams); }}
        />

        {/* Page Header */}
        <div className="text-center mb-12 mt-10">
          {selectedCollection !== 'all' ? (
            <>
              <p className="text-xs tracking-[0.4em] text-gray-400 mb-3">KOLEKSİYON</p>
              <h1 className="font-serif text-4xl md:text-5xl font-light tracking-wider mb-4" data-testid="collection-title">
                {siteCollections.find((c) => c.slug === selectedCollection)?.name || 'Koleksiyon'}
              </h1>
              <p className="text-gray-500 max-w-2xl mx-auto">
                {siteCollections.find((c) => c.slug === selectedCollection)?.description || ''}
              </p>
              <button
                onClick={() => { searchParams.delete('collection'); setSearchParams(searchParams); }}
                className="mt-4 inline-flex items-center gap-2 text-xs tracking-wider text-gray-500 hover:text-black transition-colors"
                data-testid="clear-collection-filter"
              >
                <X size={12} /> KOLEKSİYON FİLTRESİNİ KALDIR
              </button>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-light tracking-wider mb-4">ÜRÜNLER</h1>
              <p className="text-gray-500">Premium ipek eşarp ve fular koleksiyonumuz</p>
            </>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-2 text-sm tracking-wider hover:opacity-70 transition-opacity"
            >
              <Filter size={18} />
              FİLTRELE
            </button>
            <span className="text-gray-400 text-sm">{filteredProducts.length} ürün</span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1"
              >
                <X size={14} />
                Filtreleri Temizle
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-transparent pr-8 text-sm tracking-wider cursor-pointer focus:outline-none"
              >
                <option value="newest">En Yeni</option>
                <option value="price-low">Fiyat: Düşük-Yüksek</option>
                <option value="price-high">Fiyat: Yüksek-Düşük</option>
                <option value="name">A-Z</option>
              </select>
              <ChevronDown size={14} className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* View Mode */}
            <div className="hidden md:flex items-center gap-2 border-l pl-4">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'text-black' : 'text-gray-400'}`}
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'text-black' : 'text-gray-400'}`}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <aside
            className={`fixed lg:relative inset-0 lg:inset-auto z-40 lg:z-auto bg-white lg:bg-transparent lg:block w-80 lg:w-64 flex-shrink-0 transition-transform duration-300 ${
              isFilterOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
            }`}
          >
            <div className="p-6 lg:p-0 h-full overflow-y-auto">
              <div className="flex items-center justify-between lg:hidden mb-8">
                <h2 className="text-lg font-light tracking-wider">FİLTRELER</h2>
                <button onClick={() => setIsFilterOpen(false)}>
                  <X size={24} />
                </button>
              </div>

              {/* Categories */}
              <div className="mb-8">
                <h3 className="text-sm font-medium tracking-wider mb-4">KATEGORİLER</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      className={`block w-full text-left py-2 text-sm transition-colors ${
                        selectedCategory === category.id
                          ? 'text-black font-medium'
                          : 'text-gray-500 hover:text-black'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size Filter */}
              <div className="mb-8">
                <h3 className="text-sm font-medium tracking-wider mb-4">BOYUT</h3>
                <div className="space-y-2">
                  {['65x65', '75x75', '90x90'].map((size) => (
                    <label key={size} className="flex items-center gap-2 text-sm text-gray-500 hover:text-black cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={sizeFilter.includes(size)}
                        onChange={() => handleSizeFilter(size)}
                        className="rounded border-gray-300 text-black focus:ring-black" 
                      />
                      {size} cm
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-8">
                <h3 className="text-sm font-medium tracking-wider mb-4">FİYAT ARALIĞI</h3>
                <div className="space-y-2">
                  {[
                    { label: '0₺ - 2.000₺', value: '0-2000' },
                    { label: '2.000₺ - 3.000₺', value: '2000-3000' },
                    { label: '3.000₺ - 4.000₺', value: '3000-4000' },
                    { label: '4.000₺ +', value: '4000+' },
                  ].map((range) => (
                    <label key={range.value} className="flex items-center gap-2 text-sm text-gray-500 hover:text-black cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={priceFilter.includes(range.value)}
                        onChange={() => handlePriceFilter(range.value)}
                        className="rounded border-gray-300 text-black focus:ring-black" 
                      />
                      {range.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Stock Status */}
              <div>
                <h3 className="text-sm font-medium tracking-wider mb-4">STOK DURUMU</h3>
                <p className="text-xs text-gray-500">
                  Tüm ürünlerimiz sınırlı sayıda üretilmektedir. Stok durumu anlık olarak güncellenmektedir.
                </p>
              </div>
            </div>
          </aside>

          {/* Filter Overlay */}
          {isFilterOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setIsFilterOpen(false)}
            />
          )}

          {/* Product Grid */}
          <div className="flex-1">
            {/* Search Results Info */}
            {searchQuery && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <p className="text-sm">
                  <span className="text-gray-500">Arama sonuçları:</span>{' '}
                  <span className="font-medium">"{searchQuery}"</span>{' '}
                  <span className="text-gray-500">({filteredProducts.length} ürün)</span>
                </p>
                <button
                  onClick={() => {
                    searchParams.delete('search');
                    setSearchParams(searchParams);
                  }}
                  className="text-sm text-gray-500 hover:text-black flex items-center gap-1"
                >
                  <X size={14} />
                  Temizle
                </button>
              </div>
            )}

            <div
              className={`grid gap-8 ${
                viewMode === 'grid'
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                  : 'grid-cols-1'
              }`}
            >
              {filteredProducts.map((product) => {
                const inStock = hasStock(product.id);
                
                return (
                  <div key={product.id} className={viewMode === 'list' ? 'flex gap-6' : ''}>
                    <Link
                      to={`/urun/${product.id}`}
                      className={`group ${viewMode === 'list' ? 'w-48 flex-shrink-0' : ''}`}
                    >
                      <InteractiveProductImage
                        product={product}
                        className={`mb-4 ${!inStock ? 'opacity-80' : ''}`}
                      >
                        {/* Badges */}
                        <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
                          {product.isNew && inStock && (
                            <span className="bg-black text-white text-xs px-3 py-1 tracking-wider">
                              YENİ
                            </span>
                          )}
                          {!inStock && (
                            <span className="bg-red-600 text-white text-xs px-3 py-1 tracking-wider">
                              TÜKENDİ
                            </span>
                          )}
                        </div>
                        {/* Favorite Button */}
                        <button
                          onClick={(e) => handleFavoriteClick(e, product.id)}
                          className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-all z-10 ${
                            isFavorite(product.id)
                              ? 'bg-red-500 text-white'
                              : 'bg-white/80 hover:bg-white text-gray-600 hover:text-red-500'
                          }`}
                          title={isFavorite(product.id) ? 'Favorilerden kaldır' : 'Favorilere ekle'}
                        >
                          <Heart size={18} fill={isFavorite(product.id) ? 'currentColor' : 'none'} />
                        </button>
                      </InteractiveProductImage>
                    </Link>
                    <div className={viewMode === 'list' ? 'flex-1' : 'text-center'}>
                      <Link to={`/urun/${product.id}`}>
                        <h3 className="text-sm font-medium mb-2 hover:underline">{product.name}</h3>
                      </Link>
                      {viewMode === 'list' && (
                        <p className="text-sm text-gray-500 mb-4">{product.description}</p>
                      )}
                      <div className={`flex items-center gap-3 ${viewMode === 'grid' ? 'justify-center' : ''}`}>
                        <span className={`text-sm ${!inStock ? 'text-gray-400 line-through' : ''}`}>
                          {formatPrice(product.prices?.['65x65'] || product.price)}
                        </span>
                        {!inStock && (
                          <span className="text-xs text-red-500 font-medium">Stok Yok</span>
                        )}
                      </div>
                      {viewMode === 'list' && inStock && (
                        <button
                          data-testid={`buy-shopier-${product.id}`}
                          onClick={() => window.open('https://www.shopier.com/valmeraofficial', '_blank', 'noopener,noreferrer')}
                          className="mt-4 bg-black text-white px-8 py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors"
                        >
                          SATIN AL
                        </button>
                      )}
                      {viewMode === 'list' && !inStock && (
                        <button
                          disabled
                          className="mt-4 bg-gray-300 text-gray-500 px-8 py-3 text-sm tracking-wider cursor-not-allowed"
                        >
                          TÜKENDİ
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* No Results */}
            {filteredProducts.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-500 mb-4">Aradığınız kriterlere uygun ürün bulunamadı.</p>
                <button
                  onClick={clearFilters}
                  className="text-sm text-black underline hover:no-underline"
                >
                  Tüm ürünleri göster
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
